import React from "react";
import { trackEvent } from "~/utils/analytics";

export type Term = { term: string; definition: string };
export type SectionLink = {
  sectionId: string;
  sectionName: string;
  firstPageId: string;
};
export type BatesLink = {
  batesNumber: string;
  pageId: string;
};
export type SectionReferenceScope = "page" | "section";
export type SectionClickHandler = (
  sectionId: string,
  pageId: string,
  context: string,
  scope: SectionReferenceScope
) => void;

// The literal two-character escape (backslash + `n`) that API responses use in
// place of a real newline.
const LITERAL_NEWLINE_ESCAPE = "\\n";
// A backtick or tilde, i.e. a fence-run character.
const FENCE_RUN_CHAR_RE = /[`~]/;

// How a segment was separated from the previous one: a real "\n" already in the
// input, a literal `\n` escape (backslash + n), or nothing for the first
// segment. A boundary is a fence marker's structural break vs. an in-code escape
// only relative to the segments it joins (see {@link normalizeLiteralNewlines}).
type NewlineSeparator = "none" | "real" | "escape";
type ContentSegment = { text: string; separatorBefore: NewlineSeparator };

// Break `content` at every real newline AND every literal `\n` escape, recording
// which kind of boundary precedes each segment. Splitting on escapes too lets
// fence detection see an opener/closer that the API glued onto a line via `\n`
// (e.g. `foo\n```json` or a body whose closer is attached as `...\n```").
function splitIntoNewlineSegments(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  content.split("\n").forEach((physicalLine, lineIndex) => {
    physicalLine.split(LITERAL_NEWLINE_ESCAPE).forEach((text, partIndex) => {
      let separatorBefore: NewlineSeparator = "escape";
      if (lineIndex === 0 && partIndex === 0) separatorBefore = "none";
      else if (partIndex === 0) separatorBefore = "real";
      segments.push({ text, separatorBefore });
    });
  });
  return segments;
}

// Flag every segment that is body content strictly inside a preserved genuine
// code fence (a real, non-wrapper opener with a matching closer). Fence marker
// segments themselves are left false so the structural boundary attaching them
// to the body is still expanded into a real newline, yielding a well-formed
// fence. An unterminated opener is not a preserved fence.
function markPreservedFenceBodies(segments: ContentSegment[]): boolean[] {
  const insideFence = Array<boolean>(segments.length).fill(false);
  const blockTracker = createMarkdownBlockTracker();
  let openIndex = -1;

  for (let i = 0; i < segments.length; i++) {
    const line = segments[i].text;
    const trackedLine = blockTracker.update(line);
    if (
      trackedLine.opensFence &&
      trackedLine.fenceMatch !== null &&
      isGenuineFenceMarkerLine(trackedLine.fenceMatch)
    ) {
      openIndex = i;
    } else if (trackedLine.closesFence && openIndex >= 0) {
      for (let j = openIndex + 1; j < i; j++) insideFence[j] = true;
      openIndex = -1;
    } else if (trackedLine.inFence && openIndex >= 0) {
      insideFence[i] = true;
    }
  }

  return insideFence;
}

/**
 * Convert literal `\n` sequences (backslash + n) from API responses into actual
 * newline characters for proper Markdown rendering, while leaving the contents
 * of genuine fenced code blocks untouched — a code sample may legitimately
 * contain a literal `\n` escape (e.g. a JSON string `"a\nb"`) that must survive
 * verbatim rather than becoming a real line break.
 *
 * The input is split into segments at every real newline and every `\n` escape
 * (see {@link splitIntoNewlineSegments}) so fence markers glued onto a line via
 * an escape are still detected. A boundary keeps its original form only when it
 * joins two segments that are both strictly inside the same preserved genuine
 * fence; every other boundary — outside code, or the structural break attaching
 * a fence marker to its body — becomes a real newline. Wrapper fences (bare /
 * `markdown` / `md`) are therefore still normalized because `unwrapAllCodeBlocks`
 * later strips them, so their escapes must become real breaks.
 */
export const normalizeLiteralNewlines = (content: string): string => {
  if (!content) return content;

  const segments = splitIntoNewlineSegments(content);
  const insideFence = markPreservedFenceBodies(segments);

  let result = "";
  for (let i = 0; i < segments.length; i++) {
    const { text, separatorBefore } = segments[i];
    if (separatorBefore !== "none") {
      const preserveEscape = insideFence[i] && insideFence[i - 1];
      if (preserveEscape) {
        result += separatorBefore === "escape" ? LITERAL_NEWLINE_ESCAPE : "\n";
      } else {
        result += "\n";
      }
    }
    result += text;
  }

  return result;
};

const PIPE_CHAR_RE = /\|/;
const CARRIAGE_RETURN_RE = /\r\n?/g;
const TABLE_DELIMITER_CELL_RE = /^:?-*:?$/;
const TABLE_DELIMITER_CELL_HAS_CONTENT_RE = /[:\-]/;
const TABLE_SYNTHETIC_DELIMITER_CELL = "---";
const MAX_TABLE_INDENT_SPACES = 3;
const TABLE_PIPE_CHAR = "|";
const TABLE_ESCAPE_CHAR = "\\";
const BACKSLASH_CHAR = "\\";
const TABLE_INDENT_RE = /^[ \t]*/;
const BLOCKQUOTE_PREFIX_RE = /^([ \t]*>\s?)/;
const ORDERED_LIST_PREFIX_RE = /^([ \t]*\d{1,3}\.\s+)/;
const UNORDERED_LIST_PREFIX_RE = /^([ \t]*[-*+]\s+)/;
const TABLE_WRAPPER_STYLES: ReadonlyArray<{
  leading: boolean;
  trailing: boolean;
}> = [
  { leading: true, trailing: true },
  { leading: true, trailing: false },
  { leading: false, trailing: true },
  { leading: false, trailing: false },
];

type TableRowParts = {
  indentation: string;
  cells: string[];
  hasLeadingPipe: boolean;
  hasTrailingPipe: boolean;
};

type MarkdownLineParts = {
  prefix: string;
  content: string;
  hasContainerPrefix: boolean;
};

const EMPTY_LINE = "";
const TABLE_ROW_SEPARATOR = " | ";
const TABLE_ROW_OPEN = "| ";
const TABLE_ROW_CLOSE = " |";
const GLUED_TRAILING_PROSE_MIN_LENGTH = 40;
const GLUED_TRAILING_PROSE_WHITESPACE_RE = /\s/;

function splitOnUnescapedPipes(line: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let isEscaping = false;

  for (const char of line) {
    if (isEscaping) {
      cell += char;
      isEscaping = false;
      continue;
    }

    if (char === TABLE_ESCAPE_CHAR) {
      cell += char;
      isEscaping = true;
      continue;
    }

    if (char === TABLE_PIPE_CHAR) {
      cells.push(cell);
      cell = "";
      continue;
    }

    cell += char;
  }

  cells.push(cell);
  return cells;
}

function hasUnescapedTrailingPipe(line: string): boolean {
  if (!line.endsWith(TABLE_PIPE_CHAR)) return false;

  let backslashCount = 0;
  for (let i = line.length - 2; i >= 0; i--) {
    if (line[i] !== BACKSLASH_CHAR) break;
    backslashCount++;
  }

  return backslashCount % 2 === 0;
}

function splitMarkdownLinePrefix(line: string): MarkdownLineParts {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const afterIndentation = line.slice(indentation.length);

  const blockquoteMatch = afterIndentation.match(BLOCKQUOTE_PREFIX_RE);
  if (blockquoteMatch) {
    const prefix = blockquoteMatch[1];
    return {
      prefix: `${indentation}${prefix}`,
      content: afterIndentation.slice(prefix.length),
      hasContainerPrefix: true,
    };
  }

  const orderedListMatch = afterIndentation.match(ORDERED_LIST_PREFIX_RE);
  if (orderedListMatch) {
    const prefix = orderedListMatch[1];
    return {
      prefix: `${indentation}${prefix}`,
      content: afterIndentation.slice(prefix.length),
      hasContainerPrefix: true,
    };
  }

  const unorderedListMatch = afterIndentation.match(UNORDERED_LIST_PREFIX_RE);
  if (unorderedListMatch) {
    const prefix = unorderedListMatch[1];
    return {
      prefix: `${indentation}${prefix}`,
      content: afterIndentation.slice(prefix.length),
      hasContainerPrefix: true,
    };
  }

  return {
    prefix: indentation,
    content: afterIndentation,
    hasContainerPrefix: false,
  };
}

function isIndentedCodeLine(line: string): boolean {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  return (
    indentation.includes("\t") || indentation.length > MAX_TABLE_INDENT_SPACES
  );
}

function isValidFenceOpenerLine(
  line: string,
  lineParts: MarkdownLineParts
): boolean {
  const rawFenceMatch = line.match(CODE_FENCE_LINE_RE);
  return !isIndentedCodeLine(rawFenceMatch === null ? lineParts.content : line);
}

function splitTableRow(
  line: string,
  wrapperStyle: { leading: boolean; trailing: boolean }
): TableRowParts {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const trimmedLine = line.slice(indentation.length).trimEnd();
  const hasLeadingPipe =
    wrapperStyle.leading && trimmedLine.startsWith(TABLE_PIPE_CHAR);
  const hasTrailingPipe =
    wrapperStyle.trailing && hasUnescapedTrailingPipe(trimmedLine);

  let innerLine = trimmedLine;
  if (hasLeadingPipe) {
    innerLine = innerLine.substring(1);
  }
  if (hasTrailingPipe) {
    innerLine = innerLine.substring(0, innerLine.length - 1);
  }

  return {
    indentation,
    cells: splitOnUnescapedPipes(innerLine).map((cell) => cell.trim()),
    hasLeadingPipe,
    hasTrailingPipe,
  };
}

function getTableRowCandidates(line: string): TableRowParts[] {
  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const trimmedLine = line.slice(indentation.length).trimEnd();
  const hasLeadingPipe = trimmedLine.startsWith(TABLE_PIPE_CHAR);
  const hasTrailingPipe = hasUnescapedTrailingPipe(trimmedLine);

  return TABLE_WRAPPER_STYLES.filter(
    (style) =>
      (!style.leading || hasLeadingPipe) && (!style.trailing || hasTrailingPipe)
  ).map((style) => splitTableRow(line, style));
}

function isValidDelimiterRowParts(parts: TableRowParts): boolean {
  return (
    isDelimiterShapedRowParts(parts) &&
    parts.cells.every((cell) => cell.includes("-"))
  );
}

function isMalformedDelimiterRowParts(parts: TableRowParts): boolean {
  return (
    isDelimiterShapedRowParts(parts) &&
    !parts.cells.every((cell) => cell.includes("-"))
  );
}

function findMatchingDelimiterRow(
  line: string,
  expectedCellCount: number
): TableRowParts | null {
  for (const candidate of getTableRowCandidates(line)) {
    if (
      candidate.cells.length === expectedCellCount &&
      isMalformedDelimiterRowParts(candidate)
    ) {
      return candidate;
    }
  }

  return null;
}

function findMatchingEmptyDelimiterRow(
  line: string,
  expectedCellCount: number,
  wrapperStyle: { leading: boolean; trailing: boolean }
): TableRowParts | null {
  const candidate = splitTableRow(line, wrapperStyle);
  if (
    candidate.cells.length === expectedCellCount &&
    isEmptyTableRowParts(candidate)
  ) {
    return candidate;
  }

  return null;
}

function isTableBlockBoundaryLine(line: string | undefined): boolean {
  if (line === undefined) return true;

  const trimmedLine = line.trim();
  if (!trimmedLine) return true;
  if (!PIPE_CHAR_RE.test(trimmedLine)) return true;
  if (isIndentedCodeLine(line)) return true;

  return false;
}

function isDelimiterShapedRowParts(parts: TableRowParts): boolean {
  return (
    parts.cells.length > 0 &&
    parts.cells.every((cell) => TABLE_DELIMITER_CELL_RE.test(cell)) &&
    parts.cells.some((cell) => TABLE_DELIMITER_CELL_HAS_CONTENT_RE.test(cell))
  );
}

function isEmptyTableRowParts(parts: TableRowParts): boolean {
  return (
    parts.cells.length > 0 &&
    parts.cells.every((cell) => cell.trim() === EMPTY_LINE)
  );
}

// GFM's canonical wrapper interpretation: a single leading and/or trailing pipe
// (when present) is the row wrapper and does not create an empty edge cell.
const CANONICAL_TABLE_WRAPPER_STYLE = {
  leading: true,
  trailing: true,
} as const;

// Parse `line` into table cells under the canonical wrapper interpretation.
// Using one fixed interpretation for both a header and its delimiter (rather
// than independently trying every wrapper style on each) prevents mismatched
// rows from being classified as a table by mixing incompatible interpretations
// — e.g. reading `| A # B | C |` as 3 cells only to match a 3-column delimiter.
function canonicalTableCellCount(line: string): number {
  return splitTableRow(line, CANONICAL_TABLE_WRAPPER_STYLE).cells.length;
}

// True when a GFM table block begins at `lines[index]`: a pipe-bearing header
// row directly followed by a delimiter row with the same canonical column
// count. Such a block is already a valid table, so the mid-line spacing repairs
// must leave it untouched — otherwise a `#`/list/`**` marker that appears
// literally inside a cell is split onto its own line and the whole table is
// destroyed.
function tableBlockStartsAt(lines: string[], index: number): boolean {
  const header = lines[index];
  const delimiter = lines[index + 1];
  if (delimiter === undefined) return false;
  // Both rows must be plain, top-level pipe rows. `isTableBlockBoundaryLine`
  // rejects blank/non-pipe/indented-code lines (so a four-space-indented
  // delimiter is treated as indented code, not a table), and the container
  // guard leaves blockquote/list tables to the container-aware passes.
  if (isTableBlockBoundaryLine(header) || isTableBlockBoundaryLine(delimiter)) {
    return false;
  }
  if (
    splitMarkdownLinePrefix(header).hasContainerPrefix ||
    splitMarkdownLinePrefix(delimiter).hasContainerPrefix
  ) {
    return false;
  }

  const delimiterParts = splitTableRow(
    delimiter,
    CANONICAL_TABLE_WRAPPER_STYLE
  );
  return (
    (isDelimiterShapedRowParts(delimiterParts) ||
      isEmptyTableRowParts(delimiterParts)) &&
    delimiterParts.cells.length === canonicalTableCellCount(header)
  );
}

// A contiguous table body row that is safe to preserve verbatim: a plain
// (non-blank, non-container, non-indented-code, non-fence) pipe row whose cell
// count does not exceed the header's `columnCount`. Container-prefixed lines are
// left to the container-aware passes, and overflow rows are excluded so the
// trailing prose that `isolateGluedTableBlocks` later splits out still flows
// through `repairMarkdownSpacing` (otherwise a glued `#`/list marker in that
// prose would survive unrepaired).
function isPreservableTableBodyLine(
  line: string,
  columnCount: number,
  allowOverflow = false
): boolean {
  if (isTableBlockBoundaryLine(line)) return false;
  if (CODE_FENCE_LINE_RE.test(line)) return false;
  const lineParts = splitMarkdownLinePrefix(line);
  if (lineParts.hasContainerPrefix) return false;
  const candidate = getBestTableRowCandidate(lineParts.content);
  if (candidate === null) return false;
  return allowOverflow || candidate.cells.length <= columnCount;
}

type RawHtmlBlockState = {
  inPre: boolean;
  inCode: boolean;
  inComment: boolean;
};

const RAW_HTML_PRE_OPEN_RE = /<pre\b[^>]*>/i;
const RAW_HTML_CODE_OPEN_RE = /<code\b[^>]*>/i;
const RAW_HTML_PRE_CLOSE_RE = /<\/pre>/i;
const RAW_HTML_CODE_CLOSE_RE = /<\/code>/i;
const RAW_HTML_COMMENT_OPEN_RE = /^ {0,3}<!--/;
const RAW_HTML_PRE_BLOCK_OPENER_LINE_RE = /^\s*<pre\b/i;
const ATX_HEADING_LINE_RE = /^ {0,3}#{1,6}(?:\s|$)/;
const LINK_REFERENCE_DEFINITION_RE = /^\s{0,3}\[[^\]]*\]:/;

function hasRawHtmlBlockOpen(line: string): boolean {
  return (
    RAW_HTML_PRE_OPEN_RE.test(line) ||
    RAW_HTML_CODE_OPEN_RE.test(line) ||
    RAW_HTML_COMMENT_OPEN_RE.test(line)
  );
}

function isRawHtmlBlockOpenerLine(line: string): boolean {
  return RAW_HTML_PRE_BLOCK_OPENER_LINE_RE.test(line);
}

function updateRawHtmlBlockState(
  line: string,
  currentState: RawHtmlBlockState
): RawHtmlBlockState {
  const nextState = { ...currentState };

  if (RAW_HTML_PRE_OPEN_RE.test(line)) {
    nextState.inPre = true;
  }
  if (RAW_HTML_CODE_OPEN_RE.test(line)) {
    nextState.inCode = true;
  }
  if (RAW_HTML_PRE_CLOSE_RE.test(line)) {
    nextState.inPre = false;
  }
  if (RAW_HTML_CODE_CLOSE_RE.test(line)) {
    nextState.inCode = false;
  }
  if (RAW_HTML_COMMENT_OPEN_RE.test(line) && !line.includes("-->")) {
    nextState.inComment = true;
  }
  if (line.includes("-->")) {
    nextState.inComment = false;
  }

  return nextState;
}

function createMarkdownBlockTracker() {
  let fenceMarker: string | null = null;
  let fenceOpenedInContainer = false;
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
    inComment: false,
  };

  return {
    update(line: string) {
      const lineParts = splitMarkdownLinePrefix(line);
      const rawFenceMatch = line.match(CODE_FENCE_LINE_RE);
      const strippedFenceMatch = lineParts.content.match(CODE_FENCE_LINE_RE);
      const fenceMatch = rawFenceMatch ?? strippedFenceMatch;
      const inFence = fenceMarker !== null;
      const inRawHtml =
        rawHtmlBlockState.inPre ||
        rawHtmlBlockState.inCode ||
        rawHtmlBlockState.inComment;
      const closesFence =
        inFence &&
        (isPhysicalCodeFenceCloser(line, fenceMarker as string) ||
          (fenceOpenedInContainer &&
            isLenientCodeFenceCloser(
              lineParts.content,
              fenceMarker as string
            )));

      if (closesFence) {
        fenceMarker = null;
        fenceOpenedInContainer = false;
      } else if (
        !inFence &&
        !inRawHtml &&
        fenceMatch !== null &&
        isValidFenceOpenerLine(line, lineParts)
      ) {
        fenceMarker = fenceMatch[2];
        fenceOpenedInContainer =
          rawFenceMatch === null && strippedFenceMatch !== null;
      } else {
        rawHtmlBlockState = updateRawHtmlBlockState(
          lineParts.content,
          rawHtmlBlockState
        );
      }

      return {
        lineParts,
        fenceMatch,
        inFence,
        inRawHtml,
        closesFence,
        opensFence: !inFence && fenceMarker !== null,
      };
    },
  };
}

function getBestTableRowCandidate(line: string): TableRowParts | null {
  return getTableRowCandidates(line)[0] ?? null;
}

function buildTableRow(cells: string[]): string {
  return `${TABLE_ROW_OPEN}${cells.join(TABLE_ROW_SEPARATOR)}${TABLE_ROW_CLOSE}`;
}

function buildTableRowWithWrapperStyle(
  cells: string[],
  wrapperStyle: { leading: boolean; trailing: boolean }
): string {
  const core = cells.join(TABLE_ROW_SEPARATOR);
  return `${wrapperStyle.leading ? TABLE_ROW_OPEN : ""}${core}${
    wrapperStyle.trailing ? TABLE_ROW_CLOSE : ""
  }`;
}

// Matches a table-header cell whose label text is glued directly to a trailing
// alignment token, e.g. `APPLICANT INFORMATION :---` or the malformed colon-only
// form `APPLICANT INFORMATION :`. Group 1 is the label, group 2 is the trailing
// token; the token is validated separately with `isDelimiterOnlyCell`.
const GLUED_HEADER_FIRST_CELL_RE = /^(.*\S)\s+(\S+)$/;
const GLUED_HEADER_MIN_CELLS = 2;

// Matches a glued label that is itself an ATX heading, e.g.
// `### Applicant Information`. Group 1 is the `#` run, group 2 the heading text.
// Such a label is emitted as a standalone heading above the reconstructed table
// so the `#` markers are not frozen into a table cell as literal text.
const GLUED_HEADER_ATX_LABEL_RE = /^(#{1,6})\s+(.*\S)$/;

// Matches a fenced-code-block delimiter line: a run of 3+ backticks or 3+
// tildes, indented by at most 3 spaces (4+ spaces is an indented code block,
// not a fence, per CommonMark) and, when opening, carrying an arbitrary info
// string (which may contain spaces and quotes, e.g. `ts title="demo"`).
// Group 2 is the fence run itself, group 3 the info string.
const CODE_FENCE_LINE_RE = /^( {0,3})(`{3,}|~{3,})(.*)$/;
const INDENTED_FENCE_LINE_RE = /^ {4,}(`{3,}|~{3,})/;

// Info string of a wrapper-style fence that `unwrapAllCodeBlocks` may strip:
// empty (a bare fence) or the markdown wrapper tokens `markdown`/`md` that LLMs
// use to wrap a whole markdown response. Any language tag (`json`, `ts`,
// `python`), punctuated token (`c++`, `tsx{1,3}`), or rich info string
// (`ts title="demo"`) denotes a real code sample that must be preserved verbatim
// rather than unwrapped.
const WRAPPER_FENCE_INFO_RE = /^(?:markdown|md)?$/i;

// True when `fenceMatch` (a CODE_FENCE_LINE_RE match) closes a block opened
// with `openMarker`. A closing fence must use the same fence character, be at
// least as long as the opener, and carry no info string — only trailing
// whitespace is allowed after a closer (per CommonMark).
function isCodeFenceCloser(
  fenceMatch: RegExpMatchArray,
  openMarker: string
): boolean {
  const closeRun = fenceMatch[2];
  return (
    closeRun[0] === openMarker[0] &&
    closeRun.length >= openMarker.length &&
    fenceMatch[3].trim() === ""
  );
}

function isLenientCodeFenceCloser(line: string, openMarker: string): boolean {
  const match = line.trim().match(/^(`{3,}|~{3,})\s*$/);
  return (
    match !== null &&
    match[1][0] === openMarker[0] &&
    match[1].length >= openMarker.length
  );
}

function isPhysicalCodeFenceCloser(line: string, openMarker: string): boolean {
  const fenceMatch = line.match(CODE_FENCE_LINE_RE);
  return fenceMatch !== null && isCodeFenceCloser(fenceMatch, openMarker);
}

// True when a `CODE_FENCE_LINE_RE` match opens a fence whose contents
// `normalizeLiteralNewlines` should preserve verbatim: a real, physical opener
// for a block that stays code. Its info string must carry neither a literal `\n`
// escape nor a nested fence run (either means the block was collapsed onto one
// line and must still be expanded), and must not be a wrapper info (bare /
// `markdown` / `md`) — `unwrapAllCodeBlocks` strips those later, so their escapes
// still need to become real breaks.
function isGenuineFenceMarkerLine(fenceMatch: RegExpMatchArray): boolean {
  const info = fenceMatch[3];
  return (
    !info.includes(LITERAL_NEWLINE_ESCAPE) &&
    !FENCE_RUN_CHAR_RE.test(info) &&
    !WRAPPER_FENCE_INFO_RE.test(info.trim())
  );
}

// Normalizes an alignment token (valid or malformed) to a canonical form,
// mirroring the alignment logic in `normalizeTableDelimiters`. A lone `:`
// is treated as left-aligned; a two-colon token like `::` as centered.
function normalizeDelimiterToken(token: string): string {
  const left = token.startsWith(":");
  const right = token.endsWith(":") && token.length > 1;
  if (left && right) return ":---:";
  if (right) return "---:";
  if (left) return ":---";
  return "---";
}

// True for a delimiter-shaped cell, accepting both valid (`:---`, `---:`) and
// the malformed colon-only (`:`, `::`) forms the extractor sometimes emits.
function isDelimiterOnlyCell(cell: string): boolean {
  return (
    TABLE_DELIMITER_CELL_RE.test(cell) &&
    TABLE_DELIMITER_CELL_HAS_CONTENT_RE.test(cell)
  );
}

// True for a delimiter-shaped cell or an empty cell. Extractors sometimes emit
// glued delimiter rows with blank cells, e.g. `LABEL :--- |  | :--- |`; a blank
// cell is a malformed delimiter that `normalizeTableDelimiters` later
// canonicalizes to `---`, so it must not disqualify the row from repair.
function isDelimiterOrEmptyCell(cell: string): boolean {
  return cell.trim() === "" || isDelimiterOnlyCell(cell);
}

/**
 * A line whose leading prose is glued to a run of alignment tokens, e.g.
 * `APPLICANT INFORMATION :--- | :--- | :--- | :--- |`. This happens when an
 * extractor drops the newline between a table's header row and its delimiter
 * row. Returns the reconstructed header and delimiter rows, or null.
 */
function extractGluedHeaderDelimiter(content: string): {
  headingLine: string | null;
  headerCells: string[];
  delimiterCells: string[];
  columnCount: number;
} | null {
  const candidate = getBestTableRowCandidate(content);
  if (!candidate) return null;

  const { cells } = candidate;
  if (cells.length < GLUED_HEADER_MIN_CELLS) return null;

  // Every cell after the first must be an alignment token or an empty
  // (malformed) delimiter cell.
  if (!cells.slice(1).every(isDelimiterOrEmptyCell)) return null;

  // The first cell must be prose glued to a trailing alignment token; the glued
  // token itself must be a real, non-empty delimiter.
  const firstCellMatch = cells[0].match(GLUED_HEADER_FIRST_CELL_RE);
  if (!firstCellMatch) return null;
  if (!isDelimiterOnlyCell(firstCellMatch[2])) return null;

  const label = firstCellMatch[1].trim();
  if (!label) return null;

  const columnCount = cells.length;
  const delimiterTokens = [firstCellMatch[2], ...cells.slice(1)];
  const delimiterCells = delimiterTokens.map(normalizeDelimiterToken);

  // When the label is itself an ATX heading, keep it as a real heading above
  // the table (with an empty header row) instead of putting `### ...` literally
  // into a cell, where the `#` markers would render as plain text.
  const atxMatch = label.match(GLUED_HEADER_ATX_LABEL_RE);
  if (atxMatch) {
    return {
      headingLine: `${atxMatch[1]} ${atxMatch[2]}`,
      headerCells: Array<string>(columnCount).fill(""),
      delimiterCells,
      columnCount,
    };
  }

  const headerCells = [label, ...Array<string>(columnCount - 1).fill("")];

  return { headingLine: null, headerCells, delimiterCells, columnCount };
}

function isTableBodyRow(
  line: string | undefined,
  expectedCellCount: number
): boolean {
  if (line === undefined) return false;

  const lineParts = splitMarkdownLinePrefix(line);
  if (lineParts.hasContainerPrefix) return false;
  if (!PIPE_CHAR_RE.test(lineParts.content)) return false;
  if (isIndentedCodeLine(line)) return false;

  // A row with fewer cells than the reconstructed header is a genuine mismatch.
  const candidate = getBestTableRowCandidate(lineParts.content);
  if (candidate === null || candidate.cells.length < expectedCellCount) {
    return false;
  }

  // A line made up entirely of alignment tokens (or blank delimiter cells) is a
  // delimiter row, not a body row. When a well-formed table's real header
  // already looks glued (e.g. `Label :--- | :--- |` followed by its own
  // `| --- | --- |` delimiter), the next line is that delimiter row and must not
  // be treated as the body row that would trigger a rewrite.
  if (candidate.cells.every(isDelimiterOrEmptyCell)) return false;

  // A row with extra cells carries the trailing-prose extraction defect (e.g.
  // `| a | b | long prose... |` under a two-column header). Only accept it when
  // the overflow is glued trailing prose that `isolateGluedTableBlocks` will
  // actually split into its own paragraph. Otherwise the repaired table would
  // have more body cells than delimiter columns and GFM would silently drop the
  // extra value that was previously visible as raw text.
  if (
    isUnisolatableOverflowRow(lineParts.content, candidate, expectedCellCount)
  ) {
    return false;
  }

  return true;
}

// True when reconstructing an `expectedCellCount`-column table would silently
// drop cells from a body row: it has more cells than the header, and the
// overflow is not the glued trailing prose that `isolateGluedTableBlocks` would
// split into its own paragraph. GFM discards cells beyond the header width, so
// repairing such a block would lose data currently visible as raw text.
function isUnisolatableOverflowRow(
  content: string,
  candidate: TableRowParts,
  expectedCellCount: number
): boolean {
  if (candidate.cells.length <= expectedCellCount) return false;

  const splitBody = splitTableRowContentAtCellCount(
    content,
    candidate,
    expectedCellCount
  );
  return !splitBody || !isGluedTrailingProse(splitBody.after);
}

// True when a table body row after the first (which `isTableBodyRow` already
// vets) would lose data once the glued header is repaired into an
// `expectedCellCount`-column table. Scans the contiguous body block from
// `startIndex`, stopping at the first line that is not a plain table body row.
function hasCorruptibleFollowingBodyRow(
  lines: string[],
  startIndex: number,
  expectedCellCount: number
): boolean {
  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i];
    const lineParts = splitMarkdownLinePrefix(line);
    if (
      lineParts.hasContainerPrefix ||
      !PIPE_CHAR_RE.test(lineParts.content) ||
      isIndentedCodeLine(line)
    ) {
      break;
    }

    const candidate = getBestTableRowCandidate(lineParts.content);
    if (candidate === null) break;
    if (
      isUnisolatableOverflowRow(lineParts.content, candidate, expectedCellCount)
    ) {
      return true;
    }
  }

  return false;
}

function hasOverflowingFollowingBodyRow(
  lines: string[],
  startIndex: number,
  expectedCellCount: number
): boolean {
  return (
    scanFollowingBodyRows(lines, startIndex, expectedCellCount).maxCellCount >
    expectedCellCount
  );
}

function scanFollowingBodyRows(
  lines: string[],
  startIndex: number,
  expectedCellCount: number
): { maxCellCount: number; endIndex: number } {
  let maxCellCount = expectedCellCount;
  let endIndex = startIndex;
  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i];
    if (CODE_FENCE_LINE_RE.test(line)) break;
    if (ATX_HEADING_LINE_RE.test(line)) break;
    if (LINK_REFERENCE_DEFINITION_RE.test(line)) break;

    const lineParts = splitMarkdownLinePrefix(line);
    if (isRawHtmlBlockOpenerLine(lineParts.content)) break;
    if (
      lineParts.hasContainerPrefix ||
      !PIPE_CHAR_RE.test(lineParts.content) ||
      isIndentedCodeLine(line)
    ) {
      break;
    }

    const candidate = getBestTableRowCandidate(lineParts.content);
    if (candidate === null) break;
    maxCellCount = Math.max(maxCellCount, candidate.cells.length);
    endIndex = i + 1;
  }

  return { maxCellCount, endIndex };
}

function findUnescapedPipeIndex(
  line: string,
  pipeOrdinal: number
): number | null {
  if (pipeOrdinal < 1) return null;

  let isEscaping = false;
  let seenPipes = 0;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (isEscaping) {
      isEscaping = false;
      continue;
    }

    if (char === TABLE_ESCAPE_CHAR) {
      isEscaping = true;
      continue;
    }

    if (char === TABLE_PIPE_CHAR) {
      seenPipes++;
      if (seenPipes === pipeOrdinal) {
        return i;
      }
    }
  }

  return null;
}

function splitTableRowContentAtCellCount(
  line: string,
  parts: TableRowParts,
  cellCount: number
): { before: string; after: string } | null {
  if (parts.cells.length <= cellCount) return null;

  const indentation = line.match(TABLE_INDENT_RE)?.[0] ?? "";
  const trimmedLine = line.slice(indentation.length).trimEnd();
  let innerLine = trimmedLine;

  if (parts.hasLeadingPipe) {
    innerLine = innerLine.substring(1);
  }
  if (parts.hasTrailingPipe) {
    innerLine = innerLine.substring(0, innerLine.length - 1);
  }

  const boundaryIndex = findUnescapedPipeIndex(innerLine, cellCount);
  if (boundaryIndex === null) return null;

  return {
    before: innerLine.slice(0, boundaryIndex),
    after: innerLine.slice(boundaryIndex + 1),
  };
}

function isGluedTrailingProse(text: string): boolean {
  const trimmed = text.trim();
  return (
    findUnescapedPipeIndex(trimmed, 1) === null &&
    trimmed.length >= GLUED_TRAILING_PROSE_MIN_LENGTH &&
    GLUED_TRAILING_PROSE_WHITESPACE_RE.test(trimmed)
  );
}

export const unwrapAllCodeBlocks = (content: string): string => {
  if (!content) return content;

  const lines = content.split("\n");
  const out: string[] = [];

  let inFence = false;
  let fenceMarker = "";
  let blockStartIdx = -1;
  // Marker of a rich-info fence (e.g. ```ts title="demo") we are passing
  // through verbatim rather than unwrapping — see below.
  let preservedMarker: string | null = null;
  let preservedFenceOpenedInContainer = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineParts = splitMarkdownLinePrefix(line);
    const fenceMatch =
      line.match(CODE_FENCE_LINE_RE) ??
      lineParts.content.match(CODE_FENCE_LINE_RE);

    // While inside a preserved code sample, keep every line (fences included)
    // until its matching closer, so legitimate code renders as a code block.
    if (preservedMarker !== null) {
      out.push(line);
      if (
        isPhysicalCodeFenceCloser(line, preservedMarker) ||
        (preservedFenceOpenedInContainer &&
          isLenientCodeFenceCloser(lineParts.content, preservedMarker))
      ) {
        preservedMarker = null;
      }
      continue;
    }

    if (fenceMatch) {
      const marker = fenceMatch[2];
      if (!inFence) {
        if (lineParts.hasContainerPrefix) {
          preservedMarker = marker;
          preservedFenceOpenedInContainer = true;
          out.push(line);
          continue;
        }
        // Only unwrap wrapper-style fences (bare or a single simple token like
        // `markdown`). A fence with any other info string — punctuated language
        // tokens (`c++`, `tsx{1,3}`) or rich strings (`ts title="demo"`) — is a
        // real code sample: preserve it verbatim so its table-shaped contents
        // are not exposed to table repair.
        if (!WRAPPER_FENCE_INFO_RE.test(fenceMatch[3].trim())) {
          preservedMarker = marker;
          preservedFenceOpenedInContainer = false;
          out.push(line);
        } else {
          inFence = true;
          fenceMarker = marker;
          blockStartIdx = out.length;
        }
      } else if (
        isCodeFenceCloser(fenceMatch, fenceMarker) ||
        isLenientCodeFenceCloser(lineParts.content, fenceMarker)
      ) {
        const blockLines = out.slice(blockStartIdx);
        const keptBefore = out.slice(0, blockStartIdx);
        out.length = 0;
        out.push(...keptBefore, ...blockLines);

        inFence = false;
        fenceMarker = "";
        blockStartIdx = -1;
      } else {
        out.push(line);
      }
      continue;
    }

    out.push(line);
  }

  return out.join("\n");
};

export const isolateGluedTableBlocks = (content: string): string => {
  if (!content || !PIPE_CHAR_RE.test(content)) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  const normalizedLines: string[] = [];
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
    inComment: false,
  };
  let activeTableColumnCount: number | null = null;
  let fenceMarker: string | null = null;
  let fenceOpenedInContainer = false;

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const lineParts = splitMarkdownLinePrefix(line);

    // Preserve fenced code blocks verbatim: their contents may legitimately
    // contain table-shaped text (including overflow "prose" cells) that must
    // not be split out of the block.
    const rawFenceMatch = line.match(CODE_FENCE_LINE_RE);
    const strippedFenceMatch = lineParts.content.match(CODE_FENCE_LINE_RE);
    const fenceMatch = rawFenceMatch ?? strippedFenceMatch;
    if (fenceMarker !== null) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      if (
        isPhysicalCodeFenceCloser(line, fenceMarker) ||
        (fenceOpenedInContainer &&
          isLenientCodeFenceCloser(lineParts.content, fenceMarker))
      ) {
        fenceMarker = null;
      }
      continue;
    }

    const inRawHtmlBlock =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      rawHtmlBlockState.inComment;

    // A fence opener only starts a fence outside raw HTML; inside a raw
    // `<pre>`/`<code>` block a literal fence line is plain text.
    if (fenceMatch && !inRawHtmlBlock && !isIndentedCodeLine(line)) {
      fenceMarker = fenceMatch[2];
      fenceOpenedInContainer =
        rawFenceMatch === null && strippedFenceMatch !== null;
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    const shouldPreserveRawHtmlLine =
      inRawHtmlBlock || hasRawHtmlBlockOpen(lineParts.content);

    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (shouldPreserveRawHtmlLine) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    if (lineParts.hasContainerPrefix) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    if (!PIPE_CHAR_RE.test(lineParts.content)) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    if (isIndentedCodeLine(line)) {
      normalizedLines.push(line);
      activeTableColumnCount = null;
      continue;
    }

    const bestCandidate = getBestTableRowCandidate(lineParts.content);
    if (!bestCandidate) {
      normalizedLines.push(line);
      continue;
    }

    if (activeTableColumnCount === null) {
      if (!isDelimiterShapedRowParts(bestCandidate)) {
        normalizedLines.push(line);
        continue;
      }

      const previousLine = lines[index - 1];
      if (!previousLine) {
        normalizedLines.push(line);
        continue;
      }

      const previousLineParts = splitMarkdownLinePrefix(previousLine);
      if (previousLineParts.hasContainerPrefix) {
        normalizedLines.push(line);
        continue;
      }

      if (isIndentedCodeLine(previousLine)) {
        normalizedLines.push(line);
        continue;
      }

      if (!PIPE_CHAR_RE.test(previousLineParts.content)) {
        normalizedLines.push(line);
        continue;
      }

      const previousPreviousLineParts = splitMarkdownLinePrefix(
        lines[index - 2] ?? EMPTY_LINE
      );
      if (!isTableBlockBoundaryLine(previousPreviousLineParts.content)) {
        normalizedLines.push(line);
        continue;
      }

      const headerRow = getBestTableRowCandidate(previousLineParts.content);
      if (!headerRow) {
        normalizedLines.push(line);
        continue;
      }

      const delimiterCellCount = bestCandidate.cells.length;
      if (headerRow.cells.length > delimiterCellCount) {
        const splitHeader = splitTableRowContentAtCellCount(
          previousLineParts.content,
          headerRow,
          headerRow.cells.length - delimiterCellCount
        );
        if (splitHeader) {
          normalizedLines.pop();
          normalizedLines.push(
            `${previousLineParts.prefix}${splitHeader.before}`,
            EMPTY_LINE,
            buildTableRow(headerRow.cells.slice(-delimiterCellCount))
          );
        }
      }

      normalizedLines.push(line);
      activeTableColumnCount = delimiterCellCount;
      continue;
    }

    if (bestCandidate.cells.length > activeTableColumnCount) {
      const splitBody = splitTableRowContentAtCellCount(
        lineParts.content,
        bestCandidate,
        activeTableColumnCount
      );
      if (splitBody && isGluedTrailingProse(splitBody.after)) {
        const overflowCells = bestCandidate.cells.slice(activeTableColumnCount);
        if (overflowCells.some((cell) => cell.trim().length > 0)) {
          normalizedLines.push(
            buildTableRow(bestCandidate.cells.slice(0, activeTableColumnCount)),
            EMPTY_LINE,
            `${lineParts.prefix}${splitBody.after}`
          );
          activeTableColumnCount = null;
          continue;
        }
      }
    }

    normalizedLines.push(line);
  }

  return normalizedLines.join("\n");
};

/**
 * Split table rows where a header label is glued to the delimiter row on a
 * single line, e.g. `APPLICANT INFORMATION :--- | :--- | :--- | :--- |`
 * followed by pipe-delimited body rows. Extractors occasionally drop the
 * newline between the header and delimiter rows, which leaves remark-gfm
 * unable to recognise the table so it renders as raw text.
 *
 * The label becomes the first header cell (remaining header cells are left
 * empty) and the alignment tokens become a standalone delimiter row. Only a
 * line that starts a table block (its previous line is a block boundary) and is
 * directly followed by a body row with at least the header's column count is
 * rewritten, so ordinary prose and rows already inside a table are never
 * affected. A following body row with extra cells (the trailing-prose defect)
 * is still accepted; the later `isolateGluedTableBlocks` pass splits it.
 */
export const splitGluedHeaderDelimiterRows = (content: string): string => {
  if (!content || !PIPE_CHAR_RE.test(content)) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  const normalizedLines: string[] = [];
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
    inComment: false,
  };
  let fenceMarker: string | null = null;
  let fenceOpenedInContainer = false;

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const lineParts = splitMarkdownLinePrefix(line);

    // Preserve fenced code blocks verbatim: their contents may legitimately
    // contain table-shaped text that must not be rewritten.
    const fenceMatch = line.match(CODE_FENCE_LINE_RE);
    if (fenceMarker !== null) {
      normalizedLines.push(line);
      if (
        isPhysicalCodeFenceCloser(line, fenceMarker) ||
        (fenceOpenedInContainer &&
          isLenientCodeFenceCloser(lineParts.content, fenceMarker))
      ) {
        fenceMarker = null;
      }
      continue;
    }

    const inRawHtmlBlock =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      rawHtmlBlockState.inComment;

    // A fence opener only starts a fence outside raw HTML; inside a raw
    // `<pre>`/`<code>` block a literal fence line is plain text.
    if (
      fenceMatch &&
      !inRawHtmlBlock &&
      isValidFenceOpenerLine(line, lineParts)
    ) {
      fenceMarker = fenceMatch[2];
      fenceOpenedInContainer = lineParts.hasContainerPrefix;
      normalizedLines.push(line);
      continue;
    }

    const shouldPreserveRawHtmlLine =
      inRawHtmlBlock || hasRawHtmlBlockOpen(lineParts.content);

    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (
      shouldPreserveRawHtmlLine ||
      lineParts.hasContainerPrefix ||
      !PIPE_CHAR_RE.test(lineParts.content) ||
      isIndentedCodeLine(line)
    ) {
      normalizedLines.push(line);
      continue;
    }

    // Only rewrite a glued row that starts a fresh table block. If the previous
    // line is itself a table row, this line is inside an existing table and a
    // delimiter-shaped body row must not be split into header + delimiter.
    if (!isTableBlockBoundaryLine(lines[index - 1])) {
      normalizedLines.push(line);
      continue;
    }

    const glued = extractGluedHeaderDelimiter(lineParts.content);
    if (
      !glued ||
      !isTableBodyRow(lines[index + 1], glued.columnCount) ||
      hasCorruptibleFollowingBodyRow(lines, index + 2, glued.columnCount)
    ) {
      normalizedLines.push(line);
      continue;
    }

    if (glued.headingLine !== null) {
      normalizedLines.push(`${lineParts.prefix}${glued.headingLine}`);
    }
    normalizedLines.push(
      `${lineParts.prefix}${buildTableRow(glued.headerCells)}`,
      `${lineParts.prefix}${buildTableRow(glued.delimiterCells)}`
    );
  }

  return normalizedLines.join("\n");
};

export const synthesizeMissingTableHeader = (content: string): string => {
  if (!content || !PIPE_CHAR_RE.test(content)) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  const output: string[] = [];
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
    inComment: false,
  };
  let fenceMarker: string | null = null;
  let fenceOpenedInContainer = false;

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const lineParts = splitMarkdownLinePrefix(line);
    const fenceMatch =
      line.match(CODE_FENCE_LINE_RE) ??
      lineParts.content.match(CODE_FENCE_LINE_RE);
    if (fenceMarker !== null) {
      output.push(line);
      if (
        isPhysicalCodeFenceCloser(line, fenceMarker) ||
        (fenceOpenedInContainer &&
          isLenientCodeFenceCloser(lineParts.content, fenceMarker))
      ) {
        fenceMarker = null;
      }
      continue;
    }

    const inRawHtmlBlock =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      rawHtmlBlockState.inComment;
    if (
      fenceMatch &&
      !inRawHtmlBlock &&
      isValidFenceOpenerLine(line, lineParts)
    ) {
      fenceMarker = fenceMatch[2];
      fenceOpenedInContainer = lineParts.hasContainerPrefix;
      output.push(line);
      continue;
    }

    const shouldPreserveRawHtmlLine =
      inRawHtmlBlock || hasRawHtmlBlockOpen(lineParts.content);
    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (
      shouldPreserveRawHtmlLine ||
      lineParts.hasContainerPrefix ||
      isIndentedCodeLine(line) ||
      !PIPE_CHAR_RE.test(lineParts.content)
    ) {
      output.push(line);
      continue;
    }

    const candidate = getTableRowCandidates(lineParts.content).find(
      isDelimiterShapedRowParts
    );
    if (!candidate) {
      output.push(line);
      continue;
    }

    const previousLine = lines[index - 1];
    const previousLineParts = splitMarkdownLinePrefix(previousLine ?? "");
    if (
      previousLine !== undefined &&
      PIPE_CHAR_RE.test(previousLineParts.content)
    ) {
      output.push(line);
      continue;
    }

    const bodyLine = lines[index + 1];
    const bodyLineParts = splitMarkdownLinePrefix(bodyLine ?? "");
    const bodyFenceMatch =
      bodyLine?.match(CODE_FENCE_LINE_RE) ??
      bodyLineParts.content.match(CODE_FENCE_LINE_RE);
    const bodyCandidate = getBestTableRowCandidate(bodyLineParts.content);
    if (
      bodyCandidate === null ||
      LINK_REFERENCE_DEFINITION_RE.test(bodyLineParts.content) ||
      (bodyCandidate.cells.length < 2 &&
        (!bodyCandidate.hasLeadingPipe || !bodyCandidate.hasTrailingPipe)) ||
      bodyCandidate.cells.every(isDelimiterOrEmptyCell) ||
      bodyLineParts.hasContainerPrefix ||
      bodyFenceMatch !== null ||
      ATX_HEADING_LINE_RE.test(bodyLineParts.content) ||
      RAW_HTML_COMMENT_OPEN_RE.test(bodyLineParts.content) ||
      (hasRawHtmlBlockOpen(bodyLineParts.content) &&
        !PIPE_CHAR_RE.test(bodyLineParts.content)) ||
      isIndentedCodeLine(bodyLine ?? "") ||
      !PIPE_CHAR_RE.test(bodyLineParts.content)
    ) {
      output.push(line);
      continue;
    }

    output.push(
      `${lineParts.prefix}${candidate.indentation}${buildTableRowWithWrapperStyle(
        Array<string>(candidate.cells.length).fill(EMPTY_LINE),
        CANONICAL_TABLE_WRAPPER_STYLE
      )}`,
      line
    );
  }

  return output.join("\n");
};

export const widenEmptyHeaderTables = (content: string): string => {
  if (!content || !PIPE_CHAR_RE.test(content)) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  const output: string[] = [];
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
    inComment: false,
  };
  let fenceMarker: string | null = null;
  let fenceOpenedInContainer = false;

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const lineParts = splitMarkdownLinePrefix(line);
    const fenceMatch =
      line.match(CODE_FENCE_LINE_RE) ??
      lineParts.content.match(CODE_FENCE_LINE_RE);
    if (fenceMarker !== null) {
      output.push(line);
      if (
        isPhysicalCodeFenceCloser(line, fenceMarker) ||
        (fenceOpenedInContainer &&
          isLenientCodeFenceCloser(lineParts.content, fenceMarker))
      ) {
        fenceMarker = null;
      }
      continue;
    }

    const inRawHtmlBlock =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      rawHtmlBlockState.inComment;
    if (
      fenceMatch &&
      !inRawHtmlBlock &&
      isValidFenceOpenerLine(line, lineParts)
    ) {
      fenceMarker = fenceMatch[2];
      fenceOpenedInContainer = lineParts.hasContainerPrefix;
      output.push(line);
      continue;
    }

    const shouldPreserveRawHtmlLine =
      inRawHtmlBlock || hasRawHtmlBlockOpen(lineParts.content);
    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (
      shouldPreserveRawHtmlLine ||
      lineParts.hasContainerPrefix ||
      isIndentedCodeLine(line) ||
      !PIPE_CHAR_RE.test(lineParts.content)
    ) {
      output.push(line);
      continue;
    }

    const delimiterLine = lines[index + 1];
    const delimiterLineParts = splitMarkdownLinePrefix(delimiterLine ?? "");
    const delimiterCandidates = delimiterLine
      ? getTableRowCandidates(delimiterLineParts.content).filter(
          (candidate) =>
            isDelimiterShapedRowParts(candidate) ||
            isEmptyTableRowParts(candidate)
        )
      : [];
    const tablePair = getTableRowCandidates(lineParts.content)
      .filter(isEmptyTableRowParts)
      .map((headerCandidate) => ({
        header: headerCandidate,
        delimiter: delimiterCandidates.find(
          (delimiterCandidate) =>
            delimiterCandidate.cells.length === headerCandidate.cells.length
        ),
      }))
      .find((pair) => pair.delimiter !== undefined);
    const header = tablePair?.header;
    const delimiterParts = tablePair?.delimiter;
    if (
      !header ||
      !delimiterParts ||
      delimiterLineParts.hasContainerPrefix ||
      isIndentedCodeLine(delimiterLine ?? "")
    ) {
      output.push(line);
      continue;
    }

    const bodyScan = scanFollowingBodyRows(
      lines,
      index + 2,
      header.cells.length
    );
    const maxCellCount = bodyScan.maxCellCount;
    if (maxCellCount <= header.cells.length) {
      output.push(line, delimiterLine);
      output.push(...lines.slice(index + 2, bodyScan.endIndex));
      index = Math.max(index, bodyScan.endIndex - 1);
      continue;
    }

    const widenedHeader = buildTableRowWithWrapperStyle(
      Array<string>(maxCellCount).fill(EMPTY_LINE),
      {
        leading: header.hasLeadingPipe,
        trailing: header.hasTrailingPipe,
      }
    );
    const widenedDelimiter = buildTableRowWithWrapperStyle(
      [
        ...delimiterParts.cells,
        ...Array<string>(maxCellCount - delimiterParts.cells.length).fill(
          TABLE_SYNTHETIC_DELIMITER_CELL
        ),
      ],
      {
        leading: delimiterParts.hasLeadingPipe,
        trailing: delimiterParts.hasTrailingPipe,
      }
    );
    output.push(
      `${lineParts.prefix}${header.indentation}${widenedHeader}`,
      `${splitMarkdownLinePrefix(delimiterLine).prefix}${delimiterParts.indentation}${widenedDelimiter}`
    );
    index++;
  }

  return output.join("\n");
};

export const normalizeTableDelimiters = (content: string): string => {
  if (!content || !PIPE_CHAR_RE.test(content)) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
    inComment: false,
  };
  let fenceMarker: string | null = null;
  let fenceOpenedInContainer = false;

  const normalizedLines = lines.map((line, index) => {
    // Preserve fenced code blocks verbatim: their contents may legitimately
    // contain table-shaped text whose delimiter row must not be normalized.
    const lineParts = splitMarkdownLinePrefix(line);
    const fenceMatch =
      line.match(CODE_FENCE_LINE_RE) ??
      lineParts.content.match(CODE_FENCE_LINE_RE);
    if (fenceMarker !== null) {
      if (
        isPhysicalCodeFenceCloser(line, fenceMarker) ||
        (fenceOpenedInContainer &&
          isLenientCodeFenceCloser(lineParts.content, fenceMarker))
      ) {
        fenceMarker = null;
      }
      return line;
    }

    const inRawHtmlBlock =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      rawHtmlBlockState.inComment;

    // A fence opener only starts a fence outside raw HTML; inside a raw
    // `<pre>`/`<code>` block a literal fence line is plain text.
    if (fenceMatch && !inRawHtmlBlock && !isIndentedCodeLine(line)) {
      fenceMarker = fenceMatch[2];
      fenceOpenedInContainer = lineParts.hasContainerPrefix;
      return line;
    }

    const shouldPreserveRawHtmlLine =
      inRawHtmlBlock || hasRawHtmlBlockOpen(lineParts.content);

    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (shouldPreserveRawHtmlLine) return line;

    if (!PIPE_CHAR_RE.test(lineParts.content)) return line;
    if (isIndentedCodeLine(line) && !lineParts.hasContainerPrefix) {
      return line;
    }

    const previousLine = lines[index - 1];
    if (!previousLine) return line;

    const previousLineParts = splitMarkdownLinePrefix(previousLine);
    if (
      isIndentedCodeLine(previousLine) &&
      !previousLineParts.hasContainerPrefix &&
      !INDENTED_FENCE_LINE_RE.test(previousLine)
    ) {
      return line;
    }

    if (!PIPE_CHAR_RE.test(previousLineParts.content)) return line;

    const previousPreviousLineParts = splitMarkdownLinePrefix(
      lines[index - 2] ?? ""
    );
    if (!isTableBlockBoundaryLine(previousPreviousLineParts.content)) {
      return line;
    }

    const previousTableRowCandidates = getTableRowCandidates(
      previousLineParts.content
    );
    const headerRow =
      previousTableRowCandidates.find(isValidDelimiterRowParts) ??
      previousTableRowCandidates[0];

    if (!headerRow) return line;
    if (isValidDelimiterRowParts(headerRow)) return line;

    const delimiterRow = findMatchingDelimiterRow(
      lineParts.content,
      headerRow.cells.length
    );
    const canUseEmptyFallback =
      !lineParts.hasContainerPrefix && !previousLineParts.hasContainerPrefix;
    const emptyDelimiterRow =
      delimiterRow === null && canUseEmptyFallback
        ? findMatchingEmptyDelimiterRow(
            lineParts.content,
            headerRow.cells.length,
            {
              leading: headerRow.hasLeadingPipe,
              trailing: headerRow.hasTrailingPipe,
            }
          )
        : null;
    const matchingDelimiterRow = delimiterRow ?? emptyDelimiterRow;
    if (!matchingDelimiterRow) return line;
    if (
      emptyDelimiterRow !== null &&
      hasOverflowingFollowingBodyRow(lines, index + 1, headerRow.cells.length)
    ) {
      return line;
    }

    const { indentation, cells, hasLeadingPipe, hasTrailingPipe } =
      matchingDelimiterRow;

    if (cells.length === 0) return line;
    if (headerRow.cells.length !== cells.length) return line;

    const normalizedCells = cells.map((cell) => {
      if (cell.includes("-")) return cell;
      if (cell.startsWith(":") && cell.endsWith(":") && cell.length > 1) {
        return ":---:";
      }
      if (cell.endsWith(":") && cell.length > 1) {
        return "---:";
      }
      if (cell.startsWith(":")) {
        return ":---";
      }
      return TABLE_SYNTHETIC_DELIMITER_CELL;
    });

    const core = normalizedCells.join(" | ");
    const wrappedCore = `${hasLeadingPipe ? "| " : ""}${core}${hasTrailingPipe ? " |" : ""}`;

    return `${lineParts.prefix}${indentation}${wrappedCore}`;
  });

  return normalizedLines.join("\n");
};
/**
 * Ensure markdown block-level markers (list items and headings) that appear
 * mid-line get proper newlines inserted before them.
 */
export const ensureMarkdownNewlines = (content: string): string => {
  if (!content) return content;

  let result = content;

  // Insert newline before unordered list markers (- * +)
  result = result.replace(/([.!?:;,)]|(?<!\w)\])(?!\n) ?([-*+] )/g, "$1\n$2");

  // Insert newline before ordered list markers (1. 2. etc.) — limit to 1-3 digit numbers
  // to avoid matching years like "2019." as list markers
  result = result.replace(/([!?\]:;,)])(?!\n) ?(\d{1,3}\. )/g, "$1\n$2");
  result = result.replace(/(?<!\d)(\.)(?!\n) ?(\d{1,3}\. )/g, "$1\n$2");

  // After closing bold markers (**), insert newline before list markers
  result = result.replace(/(\*\*) ([-*+] )/g, "$1\n$2");
  result = result.replace(/(\*\*) (\d{1,3}\. )/g, "$1\n$2");

  result = result.replace(/([^\n]) +(#{1,6} )/g, "$1\n\n$2");

  return result;
};

export const ensureBoldHeadingSpacing = (content: string): string => {
  if (!content) return content;

  let result = content;

  result = result.replace(/(\*\*\w+(?:\s+\w+)+\*\*)([A-Z])/g, "$1\n\n$2");

  // The lookbehind suppresses the break only for an ordered-list marker — a
  // period preceded by 1-3 digits that run back to the start of a line (e.g.
  // `2.`) — so the marker is not orphaned from a following capitalized bold
  // span (`2. **Undated**`). The digit cap mirrors the 1-3 digit marker rule
  // in `ensureMarkdownNewlines` above so a 4-digit year (`2024.`) still splits.
  // The guard is scoped to `.` only: `!`/`?` are never list markers, and a
  // sentence merely ending in a number (`June 1.`) is not at a line start, so
  // both still split as intended.
  result = result.replace(
    /((?<!(?:^|\n)[ \t]*\d{1,3})\.|[!?])[ \t]*(?:\n(?!\n))?[ \t]*((?:-\s*)?\*\*[A-Z][^*]*\*\*)/g,
    "$1\n\n$2"
  );

  result = result.replace(/\n{3,}/g, "\n\n");

  return result;
};

/**
 * Apply `transform` to every part of `content` except code that must survive
 * verbatim — fenced code blocks, raw HTML `<pre>`/`<code>` blocks, and four-space
 * indented code — whose lines are copied through unchanged. The mid-line spacing
 * passes (`ensureMarkdownNewlines` / `ensureBoldHeadingSpacing`) are plain-regex
 * and not code-aware, so without this guard code containing prose-shaped text
 * like `foo. ### bar` would be silently rewritten — corrupting the on-screen
 * render and the persisted v2 `contentMdast`. Code detection mirrors the other
 * repair passes: a fence only opens outside raw HTML and closes on a matching
 * close-only fence line.
 */
const applyOutsideFencedCode = (
  content: string,
  transform: (segment: string) => string
): string => {
  if (!content) return content;

  const lines = content.replace(CARRIAGE_RETURN_RE, "\n").split("\n");
  const output: string[] = [];
  let buffer: string[] = [];
  let rawHtmlBlockState: RawHtmlBlockState = {
    inPre: false,
    inCode: false,
    inComment: false,
  };
  let fenceMarker: string | null = null;
  let fenceOpenedInContainer = false;

  const flushBuffer = () => {
    if (buffer.length === 0) return;
    output.push(transform(buffer.join("\n")));
    buffer = [];
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const lineParts = splitMarkdownLinePrefix(line);
    const fenceMatch =
      line.match(CODE_FENCE_LINE_RE) ??
      lineParts.content.match(CODE_FENCE_LINE_RE);

    if (fenceMarker !== null) {
      output.push(line);
      if (
        isPhysicalCodeFenceCloser(line, fenceMarker) ||
        (fenceOpenedInContainer &&
          isLenientCodeFenceCloser(lineParts.content, fenceMarker))
      ) {
        fenceMarker = null;
      }
      continue;
    }

    const inRawHtmlBlock =
      rawHtmlBlockState.inPre ||
      rawHtmlBlockState.inCode ||
      rawHtmlBlockState.inComment;

    if (
      fenceMatch &&
      !inRawHtmlBlock &&
      isValidFenceOpenerLine(line, lineParts)
    ) {
      flushBuffer();
      fenceMarker = fenceMatch[2];
      fenceOpenedInContainer = lineParts.hasContainerPrefix;
      output.push(line);
      continue;
    }

    const shouldPreserveRawHtmlLine =
      inRawHtmlBlock || hasRawHtmlBlockOpen(lineParts.content);

    rawHtmlBlockState = updateRawHtmlBlockState(
      lineParts.content,
      rawHtmlBlockState
    );

    if (shouldPreserveRawHtmlLine || isIndentedCodeLine(line)) {
      flushBuffer();
      output.push(line);
      continue;
    }

    // Preserve an already-valid GFM table (header + delimiter + body rows)
    // verbatim so the mid-line spacing repairs never split a `#`/list/`**`
    // marker that appears literally inside a cell — which would shatter the row
    // and destroy the table.
    if (!inRawHtmlBlock && tableBlockStartsAt(lines, index)) {
      flushBuffer();
      const columnCount = canonicalTableCellCount(line);
      output.push(line, lines[index + 1]);
      let bodyIndex = index + 2;
      const delimiterParts = splitTableRow(
        lines[index + 1],
        CANONICAL_TABLE_WRAPPER_STYLE
      );
      while (
        bodyIndex < lines.length &&
        isPreservableTableBodyLine(
          lines[bodyIndex],
          columnCount,
          isEmptyTableRowParts(
            splitTableRow(line, CANONICAL_TABLE_WRAPPER_STYLE)
          ) || isEmptyTableRowParts(delimiterParts)
        )
      ) {
        rawHtmlBlockState = updateRawHtmlBlockState(
          splitMarkdownLinePrefix(lines[bodyIndex]).content,
          rawHtmlBlockState
        );
        output.push(lines[bodyIndex]);
        bodyIndex++;
      }
      index = bodyIndex - 1;
      continue;
    }

    buffer.push(line);
  }

  flushBuffer();
  return output.join("\n");
};

/**
 * Run the mid-line spacing repairs (`ensureMarkdownNewlines` then
 * `ensureBoldHeadingSpacing`) over `content`, skipping fenced code, raw HTML, and
 * indented code so genuine code samples are never rewritten. Shared by the render
 * pipeline and the DOCX/PDF export chains so all surfaces repair spacing the same
 * way without corrupting code contents.
 */
export const repairMarkdownSpacing = (content: string): string =>
  applyOutsideFencedCode(content, (segment) =>
    ensureBoldHeadingSpacing(ensureMarkdownNewlines(segment))
  );

/**
 * The shared preprocessing chain applied to Markdown before it is rendered
 * (v1 `UnifiedMarkdown`) or parsed into report MDAST (v2). Keeping every render
 * surface on one pipeline guarantees the on-screen render, the persisted
 * `contentMdast`, and future consumers all resolve the same malformed extractor
 * output identically. The order matters:
 *   1. `normalizeLiteralNewlines` — turn literal `\n` escapes into real breaks.
 *   2. `unwrapAllCodeBlocks` — strip LLM wrapper fences so their contents are
 *      treated as Markdown (the later repair helpers are fence-aware and would
 *      otherwise freeze a wrapped table into a code node).
 *   3. `ensureMarkdownNewlines` / `ensureBoldHeadingSpacing` — break mid-line
 *      block markers (lists, headings, bold headings) onto their own lines. Run
 *      via `applyOutsideFencedCode` so genuine code fences are left untouched.
 *   4. `splitGluedHeaderDelimiterRows` → `isolateGluedTableBlocks` → split must
 *      run before isolation so trailing prose glued into a body row is isolated.
 *   5. `synthesizeMissingTableHeader` / `widenEmptyHeaderTables` — repair
 *      extractor tables with missing or undersized empty headers.
 *   6. `normalizeTableDelimiters` — canonicalize alignment tokens.
 */
export const preprocessMarkdownForRender = (content: string): string =>
  normalizeTableDelimiters(
    isolateGluedTableBlocks(
      widenEmptyHeaderTables(
        splitGluedHeaderDelimiterRows(
          repairMarkdownSpacing(
            synthesizeMissingTableHeader(
              unwrapAllCodeBlocks(normalizeLiteralNewlines(content))
            )
          )
        )
      )
    )
  );

// `prose-ol:ps-10` widens the ordered-list marker gutter so 3+ digit markers
// (e.g. `100.`) are not clipped by the `prose-sm` default `padding-inline-start`.
// See SKY-2373 — the "APPENDIX A: DOCUMENT LIST" in report view renders via
// UnifiedMarkdown and needs to accommodate lists with more than 99 items.
export const TERMS_PROSE_CLASS =
  "prose prose-sm dark:prose-invert font-sans normal-case prose-ol:ps-10";

// Reference context is fed to the context-mode bounding-box endpoint, whose
// query length is capped at 1000 characters.
const MAX_REFERENCE_CONTEXT_LENGTH = 1000;

export const normalizeHighlightText = (value: string): string =>
  value
    .normalize("NFKC")
    .replace(/[\u2018\u2019\u201b\u2032]/g, "'")
    .replace(/[\u201c\u201d\u201f\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/[`*_~]/g, "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const CITATION_LINK_SELECTOR =
  "a.source-map-link, a.bates-link, a.section-link";

// Index just past the last sentence-ending punctuation (".", "!", "?"), i.e.
// the start of the final sentence in `text`. Returns 0 when there is none.
function lastSentenceStart(text: string): number {
  const boundary = /[.!?]\s+/g;
  let start = 0;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(text)) !== null) {
    start = match.index + match[0].length;
  }
  return start;
}

/**
 * Collect the block's text on either side of the clicked link, omitting the
 * label text of the clicked link and of any sibling citation links. Reading
 * from the DOM (rather than string-searching the label) anchors on the element
 * the user actually clicked, and dropping neighbouring citation labels keeps
 * markers like "[1]" from masking sentence boundaries between citations.
 */
function splitTextAroundLink(
  block: HTMLElement,
  link: HTMLElement
): { before: string; after: string } {
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let before = "";
  let after = "";
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    const isCitationLabel =
      link.contains(node) || !!parent?.closest(CITATION_LINK_SELECTOR);
    if (!isCitationLabel) {
      const relation = link.compareDocumentPosition(node);
      if (relation & Node.DOCUMENT_POSITION_FOLLOWING) {
        after += node.textContent ?? "";
      } else {
        before += node.textContent ?? "";
      }
    }
    node = walker.nextNode();
  }
  return { before, after };
}

/**
 * Extract the sentence/idea surrounding a clicked reference link so it can be
 * used to highlight the relevant region of the source page (context heatmap).
 * Falls back to the enclosing block's text when a single sentence can't be
 * isolated.
 */
export function extractReferenceContext(link: HTMLElement): string {
  // Citations collapsed into a dropdown are portaled out of their paragraph, so
  // their enclosing block no longer carries the surrounding prose. The dropdown
  // precomputes the context from the trigger's in-paragraph position and exposes
  // it here via data-reference-context.
  const precomputed = link.closest<HTMLElement>("[data-reference-context]");
  if (precomputed) {
    return precomputed.dataset.referenceContext ?? "";
  }

  const block =
    link.closest<HTMLElement>(
      "p, li, td, th, blockquote, h1, h2, h3, h4, h5, h6"
    ) ?? link.parentElement;
  if (!block) {
    return normalizeHighlightText(link.textContent ?? "").slice(
      0,
      MAX_REFERENCE_CONTEXT_LENGTH
    );
  }

  const { before: rawBefore, after: rawAfter } = splitTextAroundLink(
    block,
    link
  );
  const before = normalizeHighlightText(rawBefore).replace(/\s+$/, "");
  const after = normalizeHighlightText(rawAfter).trim();
  const fullText = normalizeHighlightText(`${rawBefore} ${rawAfter}`).trim();
  if (!fullText) return "";

  // Citation attached to the end of its sentence (e.g. "...no complaints.[1]").
  if (/[.!?]$/.test(before)) {
    const sentence = before.slice(lastSentenceStart(before.slice(0, -1)));
    return (sentence.trim() || fullText).slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
  }

  // Citation embedded mid-sentence: take from the previous boundary in `before`
  // through the next boundary in `after`.
  const head = before.slice(lastSentenceStart(before));
  const afterMatch = after.search(/[.!?](\s|$)/);
  const tail = afterMatch >= 0 ? after.slice(0, afterMatch + 1) : after;
  const sentence = normalizeHighlightText(`${head} ${tail}`).trim();

  return (sentence || fullText).slice(0, MAX_REFERENCE_CONTEXT_LENGTH);
}

/**
 * Event-delegation hook for source-map and bates-link clicks.
 *
 * The optional `context` argument passed to the handlers carries the
 * sentence/idea surrounding the clicked reference, so callers can highlight the
 * relevant area of the source page (see `extractReferenceContext`).
 */
export function useSourceMapDelegation(
  el: HTMLElement | null,
  onSectionClick?: SectionClickHandler,
  onBatesClick?: (pageId: string, context?: string) => void
) {
  React.useEffect(() => {
    if (!el) return;

    const handler = (e: MouseEvent) => {
      const target = e.target as Element;

      const sourceMapLink = target.closest<HTMLElement>("a.source-map-link");
      if (sourceMapLink && el.contains(sourceMapLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: false, sourceType: "section" },
        });
        const sectionId = sourceMapLink.getAttribute("data-section-id") ?? "";
        const explicitPageId = sourceMapLink.getAttribute("data-page-id") ?? "";
        const firstPageId =
          sourceMapLink.getAttribute("data-first-page-id") ?? "";
        const pageId = explicitPageId || firstPageId;
        if (sectionId || pageId)
          onSectionClick?.(
            sectionId,
            pageId,
            extractReferenceContext(sourceMapLink),
            explicitPageId ? "page" : "section"
          );
        return;
      }

      const batesLink = target.closest<HTMLElement>("a.bates-link");
      if (batesLink && el.contains(batesLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: true, sourceType: "bates" },
        });
        const pageId = batesLink.getAttribute("data-bates-page-id");
        if (pageId) onBatesClick?.(pageId, extractReferenceContext(batesLink));
        return;
      }

      const sectionLink = target.closest<HTMLElement>("a.section-link");
      if (sectionLink && el.contains(sectionLink)) {
        e.preventDefault();
        trackEvent({
          eventName: "app/chat/source_citation.link/click",
          properties: { hasBatesNumber: false, sourceType: "section" },
        });
        const sectionId = sectionLink.getAttribute("data-section-id") ?? "";
        const explicitPageId = sectionLink.getAttribute("data-page-id") ?? "";
        const firstPageId =
          sectionLink.getAttribute("data-first-page-id") ?? "";
        const pageId = explicitPageId || firstPageId;
        if (sectionId || pageId)
          onSectionClick?.(
            sectionId,
            pageId,
            extractReferenceContext(sectionLink),
            explicitPageId ? "page" : "section"
          );
        return;
      }

      // Structured data accordion toggle
      const trailerHeader = target.closest<HTMLElement>(
        ".structured-data-trailer__header"
      );
      if (trailerHeader && el.contains(trailerHeader)) {
        const trailer = trailerHeader.closest(".structured-data-trailer");
        if (trailer) {
          trailer.classList.toggle("open");
        }
        return;
      }
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [el, onSectionClick, onBatesClick]);
}

/**
 * Remove `<a>...</a>` tags from text, keeping only the inner text content.
 * Strip standalone HTML tags (ul, ol, li, b, strong, i, em) while preserving their content.
 * Also convert literal \n sequences to actual newlines for proper Markdown rendering.
 */
export const stripAnchorTagsAndNormalizeNewlines = (
  content: string
): string => {
  if (!content) return content;
  return content
    .replace(/<a\b[^>]*>.*?<\/a>/gi, "")
    .replace(/<\/?(ul|ol)\b[^>]*>/gi, "")
    .replace(/<li\b[^>]*>/gi, "* ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<\/?(b|strong|i|em)\b[^>]*>/gi, "")
    .replace(/\\n/g, "\n");
};
